import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import * as facescan from "../utils/careplix-scan-sdk/facescan";
// import CarePlixLogo from "../assets/images/Logo.webp";
import { ReactComponent as SwitchCamera } from "../assets/icons/SwitchCamera.svg";
import { ReactComponent as NoCamera } from "../assets/icons/NoCamera.svg";
import { ReactComponent as Analyzing } from "../assets/icons/Analyzing.svg";
import Failure from "../assets/images/Failure.webp";
import BackArrow from "../assets/images/back-arrow.svg";
import Logo from "../assets/images/e&enterprise_logo_white.png";

const VisionFaceScan = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const playerRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const eyeBlinkConfidenceData = useRef([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [noCameraError, setNoCameraError] = useState(false);
  const [error, setError] = useState("");
  const [scanFrameData, setScanFrameData] = useState({
    type: "error",
    message: "---",
    progress: 0,
    timeElapsed: 0,
    isLiteMode: false,
    eyeBlinkConfidence: 0,
  });

  useEffect(() => {
    const controller = new AbortController();
    const isPlatformIOS = /iPhone|iPad|iPod|Macintosh|Mac/i.test(
      navigator.userAgent
    );
    setLoading(true);
    const playVideo = () => {
      if (playerRef.current?.paused) playerRef.current.play();
    };
    const pauseVideo = (reset = false) => {
      if (playerRef.current) {
        playerRef.current.pause();
        if (reset) playerRef.current.currentTime = 0;
      }
    };
    facescan.onFrame((fd) => {
      setScanFrameData(fd);
      if (fd.type === "error") {
        eyeBlinkConfidenceData.current = [];
        pauseVideo(true);
      } else {
        eyeBlinkConfidenceData.current.push({
          confidence: fd.eyeBlinkConfidence,
          timestamp: fd.timeElapsed,
          progress: fd.progress,
        });
        playVideo();
      }
    });
    facescan.onError((err) => {
      console.error(err);
      pauseVideo();
      if (
        err.message ===
        "We are not able to access the Camera. Please try again."
      )
        setNoCameraError(true);
      else setError(err.message);
    });
    facescan.onScanFinish(
      async ({
        raw_intensity,
        ppg_time,
        average_fps,
        leftEyeImageData,
        rightEyeImageData,
      }) => {
        try {
          setAnalyzing(true);
          pauseVideo();
          const addScanResp = await fetch(
            "https://sdk-demo.carenow.healthcare/vitals/add-scan",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                api_key: "CNCPjPwgEVyXIw5sA8l",
                scan_token: state?.scanToken ?? "",
                posture: "resting",
                // dob: "",
                // gender: "",
                metadata: {
                  // physiological_scores: { height: "", weight: "" },
                  ppg_time: ppg_time,
                  raw_intensity: raw_intensity,
                  device: `RPPG_CAREPLIX_FACE_${
                    isPlatformIOS ? "IOS" : "ANDROID"
                  }`,
                  fps: average_fps,
                },
                notes: { app_code: "CPVISION", client_code: state?.uniqueID },
              }),
              signal: controller.signal,
            }
          );
          const addScanRespJSON = await addScanResp.json();
          if (addScanRespJSON?.statusCode?.toString().startsWith("2")) {
            navigate("/vision-survey", {
              state: {
                name: state?.name ?? "",
                result: addScanRespJSON,
                eyeBlinkData: eyeBlinkConfidenceData.current,
                leftEyeImageData,
                rightEyeImageData,
              },
              replace: true,
            });
          } else
            throw new Error(
              addScanRespJSON?.message ?? "Failed to Fetch Result"
            );
        } catch (err) {
          console.error(err);
          setError(err?.message ?? "Some error occurred.");
        }
      }
    );
    playerRef.current?.addEventListener?.(
      isPlatformIOS ? "loadedmetadata" : "canplay",
      () => {
        if (canvasRef.current !== null && videoRef.current !== null) {
          facescan
            .startScan({
              canvasElement: canvasRef.current,
              videoElement: videoRef.current,
              strictness: 2,
            })
            .then(() => {
              console.log("Scan Started");
            })
            .catch(() => console.log("Scan Failed"))
            .finally(() => setLoading(false));
        }
      },
      { signal: controller.signal, once: true, passive: true }
    );
    return () => {
      facescan.stopScan();
      controller.abort();
    };
  }, [navigate, state]);

  return (
    <section className="relative h-screen w-screen bg-black">
      <div className="circle-blur w-96 h-96 mx-auto absolute top-16 left-0 right-0 blur-[45px]"></div>
      <video
        ref={playerRef}
        id="video-player"
        className="fixed top-24 left-1/2 -translate-x-1/2 h-[calc(100dvh-14rem)] rounded-[20px] bg-black"
        src={`${process.env.PUBLIC_URL}/during-scan-video/${
          state?.videoIndex ?? 1
        }.mp4`}
        preload="auto"
        playsInline
        disablePictureInPicture
        disableRemotePlayback
      />

      <video
        ref={videoRef}
        id="videoInput"
        className="fixed bottom-1/2 right-4 h-px w-px"
        autoPlay
        muted
        playsInline
      />
      <canvas
        ref={canvasRef}
        id="canvasOutput"
        className="fixed bottom-1/2 left-4 h-px w-px"
      />

      {scanFrameData.type === "error" &&
        (scanFrameData.message.includes("Detection of low FPS.") ? (
          <div className="fixed top-0 bottom-0 left-0 right-0 px-8 py-12 flex flex-col items-center justify-center bg-primary">
            <SwitchCamera className="h-16 w-16" />
            <p className="mt-6 text-xl font-light whitespace-pre-line">
              {scanFrameData.message.replace(/\. /g, ".\n")}
            </p>
          </div>
        ) : (
          <p className="fixed top-[40vh] -translate-y-1/2 left-8 right-8 px-1 py-0.5 rounded-full bg-error/70 text-white text-center text-sm font-medium lg:w-[50%] md:w-[50%] lg:mx-auto md:mx-auto">
            {scanFrameData.message}
          </p>
        ))}

      <div className="fixed top-0 left-0 right-0 p-4 bg-primary">
        {/* <img className="h-14" src={CarePlixLogo} alt="Powered by CarePlix" /> */}
        <div className="flex items-center absolute justify-between left-6 right-6 top-6 tab-screen">
          <div className="flex items-center">
            <Link
              to={-1}
              className=" w-9 h-9 bg-[#2D2D2D] rounded-full absolute left-0 flex flex-row justify-center items-center"
            >
              <img src={BackArrow} alt="" />
            </Link>
            <h2 className="text-xl lg:text-2xl md:text-2xl font-medium text-white ml-14">
              Vision <span className="text-secondary">AI</span>
            </h2>
          </div>
          <img src={Logo} className="h-9" alt="" />
        </div>
      </div>
      <div className="fixed bottom-4 left-6 right-6 bg-primary w-[50%] lg:mx-auto md:mx-auto">
        <div className="relative h-1 w-8/12 mx-auto rounded-full border-y bg-[#D9D9D9]">
          <div
            className="absolute left-0 top-0 bottom-0 rounded-r-full bg-secondary transition-all duration-1000"
            style={{ width: `${scanFrameData.progress}%` }}
          />
        </div>
        <div className="p-6">
          <button
            type="button"
            onClick={() => {
              facescan.stopScan();
              navigate(-1);
            }}
            className="block w-6/12 mx-auto px-6 py-2 rounded-full outline-none bg-white text-primary text-sm font-medium"
          >
            Cancel
          </button>
        </div>
      </div>

      {analyzing && (
        <div className="fixed top-0 bottom-0 left-0 right-0 px-8 py-12 flex flex-col items-center justify-center bg-black text-white text-center">
          <Analyzing className="shrink-0 h-32" />
          <h4 className="mt-8 text-secondary text-xl font-bold">
            Analyzing Data
          </h4>
          <p className="mt-4 text-white font-light">
            Continuing analysis for
            <br />
            accurate measurements...
          </p>
        </div>
      )}

      {error.length > 0 && (
        <div className="fixed top-0 bottom-0 left-0 right-0 px-8 py-12 flex flex-col items-center justify-center bg-black text-white text-center">
          <img className="shrink-0 h-48" src={Failure} alt="Failure..." />
          <p className="mt-8 text-secondary text-xl font-bold">
            Sorry, we failed to calculate
            <br />
            your measurements
          </p>
          <p className="mt-4 max-h-40 overflow-auto whitespace-pre-line text-white font-light">
            {error}
          </p>
          <button
            className="mt-8 w-60 px-5 py-2.5 rounded-full bg-white text-primary font-medium"
            type="button"
            onClick={() => {
              facescan.stopScan();
              navigate(-1);
            }}
          >
            Try Again
          </button>
        </div>
      )}

      {noCameraError && (
        <div className="fixed top-0 bottom-0 left-0 right-0 px-8 py-12 flex flex-col items-center justify-center bg-black text-white text-center">
          <NoCamera className="shrink-0 h-40" />
          <p className="mt-8 text-secondary text-xl font-bold">
            No Camera
            <br />
            Permission Found
          </p>
          <p className="mt-4 max-h-40 overflow-auto whitespace-pre-line text-white font-light">
            We are not able to access the Camera. Please try again.
          </p>
          <button
            className="mt-8 w-60 px-5 py-2.5 rounded-lg bg-white text-primary font-medium"
            type="button"
            onClick={() => {
              facescan.stopScan();
              navigate(-1);
            }}
          >
            Go Back
          </button>
        </div>
      )}

      {loading && (
        <div className="fixed top-0 bottom-0 left-0 right-0 px-8 py-12 flex flex-col items-center justify-center bg-white text-center">
          <svg
            className="shrink-0 h-16 text-secondary animate-spin"
            fill="none"
            viewBox="0 0 200 200"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <linearGradient id="spinner-secondHalf">
                <stop offset="0%" stopOpacity="0" stopColor="currentColor" />
                <stop
                  offset="100%"
                  stopOpacity="0.5"
                  stopColor="currentColor"
                />
              </linearGradient>
              <linearGradient id="spinner-firstHalf">
                <stop offset="0%" stopOpacity="1" stopColor="currentColor" />
                <stop
                  offset="100%"
                  stopOpacity="0.5"
                  stopColor="currentColor"
                />
              </linearGradient>
            </defs>
            <g strokeWidth="8">
              <path
                stroke="url(#spinner-secondHalf)"
                d="M 4 100 A 96 96 0 0 1 196 100"
              />
              <path
                stroke="url(#spinner-firstHalf)"
                d="M 196 100 A 96 96 0 0 1 4 100"
              />
              <path
                stroke="currentColor"
                strokeLinecap="round"
                d="M 4 100 A 96 96 0 0 1 4 98"
              />
            </g>
          </svg>
          <p className="mt-6 text-primary font-light">Loading....</p>
        </div>
      )}
    </section>
  );
};

export default VisionFaceScan;
